import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Loader2, Search, Trash2, Plus, CheckCircle2, XCircle } from 'lucide-react';
import { searchBotsByName } from '@/services/bot-checker-service';

interface Bot {
  id: string;
  name: string;
  signal_token: string;
  type: string;
}

interface BulkAddMappingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  bots: Bot[];
  onComplete: () => void;
}

export const BulkAddMappingDialog: React.FC<BulkAddMappingDialogProps> = ({
  open,
  onOpenChange,
  bots,
  onComplete
}) => {
  const [mode, setMode] = useState<'one-to-many' | 'multiple-pairs'>('one-to-many');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // One-to-many mode states
  const [parentBotId, setParentBotId] = useState('');
  const [parentBotType, setParentBotType] = useState('all');
  const [parentSearchQuery, setParentSearchQuery] = useState('');
  const [selectedChildIds, setSelectedChildIds] = useState<string[]>([]);
  const [childBotType, setChildBotType] = useState('all');
  const [childSearchQuery, setChildSearchQuery] = useState('');
  const [addDefaultEndpoint, setAddDefaultEndpoint] = useState(true);
  const [bulkNotes, setBulkNotes] = useState('');
  
  // Bulk name search states
  const [childSelectionMode, setChildSelectionMode] = useState<'browse' | 'bulk-search'>('browse');
  const [bulkSearchInput, setBulkSearchInput] = useState('');
  const [bulkSearchResults, setBulkSearchResults] = useState<Array<{
    inputName: string;
    found: boolean;
    bot?: Bot;
  }>>([]);
  const [isSearching, setIsSearching] = useState(false);

  // Multiple pairs mode states
  const [pairs, setPairs] = useState<Array<{ parent_id: string; child_id: string; notes: string }>>([
    { parent_id: '', child_id: '', notes: '' }
  ]);

  // Filter parent bots
  const filteredParentBots = bots.filter(bot => {
    const matchesType = parentBotType === 'all' || bot.type === parentBotType;
    const matchesSearch = parentSearchQuery === '' ||
      bot.name.toLowerCase().includes(parentSearchQuery.toLowerCase());
    return matchesType && matchesSearch;
  });

  // Filter child bots (exclude selected parent in one-to-many mode)
  const filteredChildBots = bots
    .filter(bot => bot.id !== parentBotId)
    .filter(bot => {
      const matchesType = childBotType === 'all' || bot.type === childBotType;
      const matchesSearch = childSearchQuery === '' ||
        bot.name.toLowerCase().includes(childSearchQuery.toLowerCase());
      return matchesType && matchesSearch;
    });

  const toggleChildSelection = (childId: string) => {
    setSelectedChildIds(prev =>
      prev.includes(childId) ? prev.filter(id => id !== childId) : [...prev, childId]
    );
  };

  const selectAllChildren = () => {
    setSelectedChildIds(filteredChildBots.map(bot => bot.id));
  };

  const deselectAllChildren = () => {
    setSelectedChildIds([]);
  };

  const handleBulkSearch = async () => {
    setIsSearching(true);
    const botNames = bulkSearchInput
      .split('\n')
      .map(name => name.trim())
      .filter(name => name.length > 0);
    
    const results = [];
    for (const name of botNames) {
      try {
        const foundBots = await searchBotsByName(name);
        // Find exact match (case-insensitive)
        const exactMatch = foundBots.find(
          bot => bot.name.toLowerCase() === name.toLowerCase()
        );
        
        results.push({
          inputName: name,
          found: !!exactMatch,
          bot: exactMatch ? {
            id: exactMatch.id,
            name: exactMatch.name,
            signal_token: exactMatch.signal_token,
            type: exactMatch.type
          } : undefined
        });
      } catch (error) {
        results.push({
          inputName: name,
          found: false
        });
      }
    }
    
    setBulkSearchResults(results);
    setIsSearching(false);
  };

  const selectAllFoundBots = () => {
    const foundBotIds = bulkSearchResults
      .filter(r => r.found && r.bot && r.bot.id !== parentBotId)
      .map(r => r.bot!.id);
    setSelectedChildIds(foundBotIds);
  };

  const handleBulkCreate = async () => {
    if (mode === 'one-to-many') {
      if (!parentBotId) {
        toast.error('Please select a parent bot');
        return;
      }
      if (selectedChildIds.length === 0) {
        toast.error('Please select at least one child bot');
        return;
      }

      setIsSubmitting(true);

      try {
        const user = await supabase.auth.getUser();
        const userId = user.data.user?.id;

        // Check existing mappings
        const { data: existingMappings } = await supabase
          .from('bot_merge_mapping')
          .select('parent_bot_id, child_bot_id')
          .eq('parent_bot_id', parentBotId)
          .in('child_bot_id', selectedChildIds);

        // Filter out duplicates
        const existingPairs = new Set(
          existingMappings?.map(m => `${m.parent_bot_id}-${m.child_bot_id}`) || []
        );

        const mappingsToInsert = selectedChildIds
          .filter(childId => !existingPairs.has(`${parentBotId}-${childId}`))
          .map(childId => ({
            parent_bot_id: parentBotId,
            child_bot_id: childId,
            notes: bulkNotes || null,
            created_by: userId
          }));

        let insertedMappings = null;
        
        // Insert only new mappings
        if (mappingsToInsert.length > 0) {
          const { data, error: mappingsError } = await supabase
            .from('bot_merge_mapping')
            .insert(mappingsToInsert)
            .select();

          if (mappingsError) throw mappingsError;
          insertedMappings = data;

          // Add default endpoints if requested
          if (addDefaultEndpoint && insertedMappings) {
            const endpointsToInsert = insertedMappings.map(mapping => ({
              mapping_id: mapping.id,
              name: 'Signal Tracking',
              url: 'https://lbvhywxvthwgiebjsrlr.supabase.co/functions/v1/bot-signal-webhook',
              timeout_ms: 30000,
              is_active: true
            }));

            const { error: endpointsError } = await supabase
              .from('bot_signal_forward_endpoints')
              .insert(endpointsToInsert);

            if (endpointsError) throw endpointsError;
          }
        }

        // Show detailed success message
        const skippedCount = selectedChildIds.length - mappingsToInsert.length;
        if (mappingsToInsert.length > 0) {
          toast.success(
            `Created ${mappingsToInsert.length} mapping${mappingsToInsert.length > 1 ? 's' : ''}` +
            (skippedCount > 0 ? `, skipped ${skippedCount} (already exist)` : '')
          );
        } else {
          toast.info('All mappings already exist');
        }

        onComplete();
        onOpenChange(false);

        // Reset form
        setParentBotId('');
        setSelectedChildIds([]);
        setBulkNotes('');
        setParentSearchQuery('');
        setChildSearchQuery('');
        setBulkSearchInput('');
        setBulkSearchResults([]);
      } catch (error: any) {
        toast.error(`Failed to create mappings: ${error.message}`);
      } finally {
        setIsSubmitting(false);
      }
    } else {
      // Multiple pairs mode
      const validPairs = pairs.filter(p => p.parent_id && p.child_id);
      if (validPairs.length === 0) {
        toast.error('Please configure at least one valid parent-child pair');
        return;
      }

      setIsSubmitting(true);

      try {
        const user = await supabase.auth.getUser();
        const userId = user.data.user?.id;

        // Check existing mappings for all pairs
        const pairChecks = validPairs.map(pair => 
          `(parent_bot_id.eq.${pair.parent_id},child_bot_id.eq.${pair.child_id})`
        );
        
        const { data: existingMappings } = await supabase
          .from('bot_merge_mapping')
          .select('parent_bot_id, child_bot_id')
          .or(pairChecks.join(','));

        // Filter out duplicates
        const existingPairs = new Set(
          existingMappings?.map(m => `${m.parent_bot_id}-${m.child_bot_id}`) || []
        );

        const newPairs = validPairs.filter(pair => 
          !existingPairs.has(`${pair.parent_id}-${pair.child_id}`)
        );

        const mappingsToInsert = newPairs.map(pair => ({
          parent_bot_id: pair.parent_id,
          child_bot_id: pair.child_id,
          notes: pair.notes || null,
          created_by: userId
        }));

        let insertedMappings = null;

        // Insert only new mappings
        if (mappingsToInsert.length > 0) {
          const { data, error: mappingsError } = await supabase
            .from('bot_merge_mapping')
            .insert(mappingsToInsert)
            .select();

          if (mappingsError) throw mappingsError;
          insertedMappings = data;

          // Add default endpoints if requested
          if (addDefaultEndpoint && insertedMappings) {
            const endpointsToInsert = insertedMappings.map(mapping => ({
              mapping_id: mapping.id,
              name: 'Signal Tracking',
              url: 'https://lbvhywxvthwgiebjsrlr.supabase.co/functions/v1/bot-signal-webhook',
              timeout_ms: 30000,
              is_active: true
            }));

            const { error: endpointsError } = await supabase
              .from('bot_signal_forward_endpoints')
              .insert(endpointsToInsert);

            if (endpointsError) throw endpointsError;
          }
        }

        // Show detailed success message
        const skippedCount = validPairs.length - newPairs.length;
        if (newPairs.length > 0) {
          toast.success(
            `Created ${newPairs.length} mapping${newPairs.length > 1 ? 's' : ''}` +
            (skippedCount > 0 ? `, skipped ${skippedCount} (already exist)` : '')
          );
        } else {
          toast.info('All mappings already exist');
        }

        onComplete();
        onOpenChange(false);

        // Reset form
        setPairs([{ parent_id: '', child_id: '', notes: '' }]);
      } catch (error: any) {
        toast.error(`Failed to create mappings: ${error.message}`);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Bulk Add Bot Mappings</DialogTitle>
          <DialogDescription>
            Create multiple bot mappings at once
          </DialogDescription>
        </DialogHeader>

        <Tabs value={mode} onValueChange={(v: any) => setMode(v)}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="one-to-many">One Parent → Many Children</TabsTrigger>
            <TabsTrigger value="multiple-pairs">Multiple Pairs</TabsTrigger>
          </TabsList>

          {/* One-to-Many Mode */}
          <TabsContent value="one-to-many" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Select Parent Bot</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Select value={parentBotType} onValueChange={setParentBotType}>
                  <SelectTrigger>
                    <SelectValue placeholder="Filter by type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="user_bot">User Bot</SelectItem>
                    <SelectItem value="prop_bot">Prop Bot</SelectItem>
                    <SelectItem value="shadow_bot">Shadow Bot</SelectItem>
                    <SelectItem value="premium_bot">Premium Bot</SelectItem>
                  </SelectContent>
                </Select>

                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search parent bots..."
                    value={parentSearchQuery}
                    onChange={(e) => setParentSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>

                <Select value={parentBotId} onValueChange={setParentBotId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select parent bot" />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredParentBots.map(bot => (
                      <SelectItem key={bot.id} value={bot.id}>
                        {bot.name} ({bot.type})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Select Child Bots</CardTitle>
              </CardHeader>
              <CardContent>
                <Tabs value={childSelectionMode} onValueChange={(v: any) => setChildSelectionMode(v)}>
                  <TabsList className="grid w-full grid-cols-2 mb-4">
                    <TabsTrigger value="browse">Browse & Select</TabsTrigger>
                    <TabsTrigger value="bulk-search">Bulk Name Search</TabsTrigger>
                  </TabsList>
                  
                  {/* Tab 1: Browse & Select (existing functionality) */}
                  <TabsContent value="browse" className="space-y-3">
                    <Select value={childBotType} onValueChange={setChildBotType}>
                      <SelectTrigger>
                        <SelectValue placeholder="Filter by type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Types</SelectItem>
                        <SelectItem value="user_bot">User Bot</SelectItem>
                        <SelectItem value="prop_bot">Prop Bot</SelectItem>
                        <SelectItem value="shadow_bot">Shadow Bot</SelectItem>
                        <SelectItem value="premium_bot">Premium Bot</SelectItem>
                      </SelectContent>
                    </Select>

                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Search child bots..."
                        value={childSearchQuery}
                        onChange={(e) => setChildSearchQuery(e.target.value)}
                        className="pl-9"
                      />
                    </div>

                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={selectAllChildren}>
                        Select All ({filteredChildBots.length})
                      </Button>
                      <Button variant="outline" size="sm" onClick={deselectAllChildren}>
                        Deselect All
                      </Button>
                      <Badge variant="secondary" className="ml-auto">
                        {selectedChildIds.length} selected
                      </Badge>
                    </div>

                    <ScrollArea className="h-[200px] border rounded-lg">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-12">
                              <Checkbox
                                checked={filteredChildBots.length > 0 && selectedChildIds.length === filteredChildBots.length}
                                onCheckedChange={(checked) => checked ? selectAllChildren() : deselectAllChildren()}
                              />
                            </TableHead>
                            <TableHead>Bot Name</TableHead>
                            <TableHead>Type</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filteredChildBots.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={3} className="text-center text-muted-foreground">
                                {parentBotId ? 'No child bots available' : 'Please select a parent bot first'}
                              </TableCell>
                            </TableRow>
                          ) : (
                            filteredChildBots.map(bot => (
                              <TableRow key={bot.id}>
                                <TableCell>
                                  <Checkbox
                                    checked={selectedChildIds.includes(bot.id)}
                                    onCheckedChange={() => toggleChildSelection(bot.id)}
                                  />
                                </TableCell>
                                <TableCell className="font-medium">{bot.name}</TableCell>
                                <TableCell>
                                  <Badge variant="outline">{bot.type}</Badge>
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </ScrollArea>
                  </TabsContent>
                  
                  {/* Tab 2: Bulk Name Search (new functionality) */}
                  <TabsContent value="bulk-search" className="space-y-3">
                    <div>
                      <Label>Bot Names (one per line)</Label>
                      <Textarea
                        value={bulkSearchInput}
                        onChange={(e) => setBulkSearchInput(e.target.value)}
                        placeholder="EURUSD-0.21_0.3-SW-1M
EURUSD-0.21_0.3-TR-1M
GBPUSD-0.21_0.3-SW-1M
GBPUSD-0.21_0.3-TR-1M
USDCHF-0.25_0.36-SW-1M
..."
                        rows={8}
                        className="font-mono text-sm"
                      />
                      <div className="flex items-center justify-between mt-2">
                        <Badge variant="secondary">
                          {bulkSearchInput.split('\n').filter(n => n.trim()).length} names
                        </Badge>
                        <Button 
                          onClick={handleBulkSearch}
                          disabled={isSearching || !bulkSearchInput.trim()}
                          size="sm"
                        >
                          {isSearching && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                          Search Bots
                        </Button>
                      </div>
                    </div>
                    
                    {bulkSearchResults.length > 0 && (
                      <div className="space-y-2">
                        <div className="flex gap-2 flex-wrap">
                          <Badge variant="default" className="bg-green-600">
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            Found: {bulkSearchResults.filter(r => r.found).length}
                          </Badge>
                          <Badge variant="destructive">
                            <XCircle className="h-3 w-3 mr-1" />
                            Not found: {bulkSearchResults.filter(r => !r.found).length}
                          </Badge>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={selectAllFoundBots}
                            className="ml-auto"
                          >
                            Select All Found
                          </Button>
                        </div>
                        
                        <ScrollArea className="h-[250px] border rounded-lg">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead className="w-12"></TableHead>
                                <TableHead className="w-12">Status</TableHead>
                                <TableHead>Input Name</TableHead>
                                <TableHead>Found Bot</TableHead>
                                <TableHead>Type</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {bulkSearchResults.map((result, idx) => (
                                <TableRow key={idx}>
                                  <TableCell>
                                    {result.found && result.bot && result.bot.id !== parentBotId && (
                                      <Checkbox
                                        checked={selectedChildIds.includes(result.bot.id)}
                                        onCheckedChange={() => toggleChildSelection(result.bot!.id)}
                                      />
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    {result.found ? (
                                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                                    ) : (
                                      <XCircle className="h-4 w-4 text-destructive" />
                                    )}
                                  </TableCell>
                                  <TableCell className="font-mono text-sm">
                                    {result.inputName}
                                  </TableCell>
                                  <TableCell>
                                    {result.found && result.bot ? (
                                      result.bot.id === parentBotId ? (
                                        <span className="text-muted-foreground">(Same as parent)</span>
                                      ) : (
                                        <span className="font-medium">{result.bot.name}</span>
                                      )
                                    ) : (
                                      <span className="text-muted-foreground">Not found</span>
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    {result.found && result.bot && (
                                      <Badge variant="outline">{result.bot.type}</Badge>
                                    )}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </ScrollArea>
                      </div>
                    )}
                  </TabsContent>
                </Tabs>
                
                {/* Notes and endpoint checkbox - outside tabs */}
                <div className="mt-4 space-y-3">
                  <div>
                    <Label>Notes (Optional)</Label>
                    <Textarea
                      value={bulkNotes}
                      onChange={(e) => setBulkNotes(e.target.value)}
                      placeholder="Add notes for all mappings..."
                      rows={2}
                    />
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="add-endpoint-bulk"
                      checked={addDefaultEndpoint}
                      onCheckedChange={(checked) => setAddDefaultEndpoint(checked as boolean)}
                    />
                    <Label htmlFor="add-endpoint-bulk" className="text-sm cursor-pointer">
                      Add default "Signal Tracking" endpoint for each mapping
                    </Label>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Multiple Pairs Mode */}
          <TabsContent value="multiple-pairs" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Configure Parent-Child Pairs</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <ScrollArea className="h-[350px]">
                  <div className="space-y-3 pr-4">
                    {pairs.map((pair, idx) => (
                      <Card key={idx} className="p-3">
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <Label className="text-sm font-semibold">Pair {idx + 1}</Label>
                            {pairs.length > 1 && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setPairs(pairs.filter((_, i) => i !== idx))}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <Label className="text-xs">Parent Bot</Label>
                              <Select
                                value={pair.parent_id}
                                onValueChange={(v) => {
                                  const newPairs = [...pairs];
                                  newPairs[idx].parent_id = v;
                                  setPairs(newPairs);
                                }}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Select parent" />
                                </SelectTrigger>
                                <SelectContent>
                                  {bots.map(bot => (
                                    <SelectItem key={bot.id} value={bot.id}>
                                      {bot.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="text-xs">Child Bot</Label>
                              <Select
                                value={pair.child_id}
                                onValueChange={(v) => {
                                  const newPairs = [...pairs];
                                  newPairs[idx].child_id = v;
                                  setPairs(newPairs);
                                }}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Select child" />
                                </SelectTrigger>
                                <SelectContent>
                                  {bots.filter(b => b.id !== pair.parent_id).map(bot => (
                                    <SelectItem key={bot.id} value={bot.id}>
                                      {bot.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          <Input
                            placeholder="Notes (optional)"
                            value={pair.notes}
                            onChange={(e) => {
                              const newPairs = [...pairs];
                              newPairs[idx].notes = e.target.value;
                              setPairs(newPairs);
                            }}
                          />
                        </div>
                      </Card>
                    ))}
                  </div>
                </ScrollArea>

                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => setPairs([...pairs, { parent_id: '', child_id: '', notes: '' }])}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Another Pair
                </Button>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="add-endpoint-pairs"
                    checked={addDefaultEndpoint}
                    onCheckedChange={(checked) => setAddDefaultEndpoint(checked as boolean)}
                  />
                  <Label htmlFor="add-endpoint-pairs" className="text-sm cursor-pointer">
                    Add default "Signal Tracking" endpoint for each mapping
                  </Label>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleBulkCreate}
            disabled={isSubmitting || (mode === 'one-to-many' ? !parentBotId || selectedChildIds.length === 0 : pairs.filter(p => p.parent_id && p.child_id).length === 0)}
          >
            {isSubmitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            {mode === 'one-to-many'
              ? `Create ${selectedChildIds.length} Mapping${selectedChildIds.length !== 1 ? 's' : ''}`
              : `Create ${pairs.filter(p => p.parent_id && p.child_id).length} Mapping${pairs.filter(p => p.parent_id && p.child_id).length !== 1 ? 's' : ''}`
            }
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
